import { useCallback, useMemo, useState } from 'react';
import last from 'lodash/last';

/**
 * @param {import('./index').Props<unknown>} props
 */
export default function useLogic(props) {
    const {
        data,
        value: externalSelectedId,
        onChange,
        getId,
        disabled,
        getDisplayName,
        onUploadConfirm: uploadObject,
        refresh,
    } = props;

    const [isOpen, setOpen] = useState(false);
    const [internalSelectedId, setInternalSelectedId] = useState(null);

    const onFieldClick = useCallback(event => {
        if (disabled) return;

        event.preventDefault();
        event.stopPropagation();

        setOpen(true);
        setInternalSelectedId(externalSelectedId);
    }, [externalSelectedId, disabled]);

    const getObjectById = useCallback(id => {
        return data.find(o => getId(o) === id) ?? null;
    }, [data]);

    const externalObject = useMemo(() => {
        return getObjectById(externalSelectedId);
    }, [externalSelectedId, getObjectById]);

    const internalObject = useMemo(() => {
        return getObjectById(internalSelectedId);
    }, [internalSelectedId, getObjectById]);

    const selectionModel = useMemo(() => {
        return internalSelectedId ? [internalSelectedId] : [];
    }, [internalSelectedId]);

    const onSelectionModelChange = useCallback(selectedIds => {
        const id = last(selectedIds);

        setInternalSelectedId(id);
    }, [getObjectById]);

    const onConfirm = () => {
        const obj = getObjectById(internalSelectedId);

        onChange(internalSelectedId, obj)
        setOpen(false);
    };

    const onUploadConfirm = useCallback(async (...args) => {
        const id = await uploadObject(...args);
        refresh();
        await new Promise(resolve => setTimeout(resolve, 200));
        setInternalSelectedId(id);
    }, [uploadObject, refresh]);

    return {
        isOpen,
        onFieldClick,
        fieldText: externalObject ? getDisplayName(externalObject) : '',
        hasSelected: !!internalObject,
        selectionText: internalObject ? getDisplayName(internalObject) : '',
        selectionModel,
        onSelectionModelChange,
        onConfirm,
        onUploadConfirm,
        setOpen,
    };
}